import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  Shield,
  PawPrint,
  Plus,
} from "lucide-react";
import {
  CarSeatIcon,
  PolisherIcon,
  HeadlightIcon,
  EngineIcon,
  UndercarriageIcon,
  CeramicSealantIcon,
  WhatsAppIcon,
} from "@/components/oneyork/icons";
import AddonModal from "@/components/oneyork/AddonModal";
import FeynlabModal from "@/components/oneyork/FeynlabModal";
import MobileParallax from "@/components/oneyork/MobileParallax";
import { SERVICES, ADDITIONAL_SERVICES, BRAND, waLink } from "@/lib/oneyork";

const ADDITIONAL_ICONS = {
  "Windshield Ceramic Coating": Shield,
  "Leather Seat Treatment": CarSeatIcon,
  "Paint Correction": PolisherIcon,
  "Headlight Restoration": HeadlightIcon,
  "Engine Bay Detailing": EngineIcon,
  "Undercarriage Wash": UndercarriageIcon,
  "Exterior Ceramic Sealant": CeramicSealantIcon,
  "Pet Hair Removal": PawPrint,
};

function ServiceRow({ service, index, onMoreInfo }) {
  const reversed = index % 2 === 1;
  const num = String(index + 1).padStart(2, "0");

  return (
    <motion.div
      id={service.key === "ceramic" ? "ceramic" : "detailing"}
      data-testid={`service-card-${service.key}`}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="relative py-14 lg:py-20 border-t border-white/10 first:border-t-0 scroll-mt-24 lg:scroll-mt-28"
    >
      {/* FEYNLAB product lineup — positioned per breakpoint */}
      {service.key === "ceramic" && (
        <img
          src="/images/feynlab-kit.png"
          alt=""
          loading="lazy"
          aria-hidden
          className="
            pointer-events-none absolute z-20 object-contain h-auto
            right-3 sm:right-5 -bottom-7 sm:-bottom-9 w-[200px] sm:w-[240px] opacity-90
            lg:right-0 lg:-top-[150px] xl:-top-[180px] lg:bottom-auto lg:w-[280px] xl:w-[320px]
          "
        />
      )}

      {/* TITLE — full width on mobile, moves into content column on desktop */}
      <div className="lg:hidden mb-10">
        <div className="flex items-center gap-3 mb-5">
          <span className="font-mono text-xs text-[#d4af37]">{num}</span>
          <span className="block w-8 h-px bg-[#d4af37]/60" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-white/50">
            {service.eyebrow}
          </span>
        </div>
        <h3 className="font-display font-bold text-5xl sm:text-6xl tracking-tighter leading-[0.92] text-white">
          {service.title}
        </h3>
        {service.key === "ceramic" && (
          <div
            data-testid="hot-topic-badge-mobile"
            className="inline-flex items-center gap-3 mt-5"
          >
            <span className="relative flex h-4 w-4">
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-80 animate-ping-wide" />
              <span className="relative inline-flex h-4 w-4 rounded-full bg-red-500" />
            </span>
            <span className="text-sm sm:text-base uppercase tracking-[0.28em] text-red-500 font-bold">
              Hot Topic
            </span>
          </div>
        )}
      </div>

      {/* IMAGE + CONTENT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-start">
        <MobileParallax
          intensity={130}
          dim={0.94}
          className={`relative overflow-hidden rounded-3xl aspect-square lg:col-span-6 ${
            reversed ? "lg:order-2" : ""
          }`}
        >
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.04] lg:scale-110"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
          {/* Desktop-only edge fade — softens the side that meets the dark content panel */}
          <div
            className={`hidden lg:block pointer-events-none absolute inset-y-0 w-[24%] ${
              reversed
                ? "left-0 bg-gradient-to-r"
                : "right-0 bg-gradient-to-l"
            } from-[#050505] via-[#050505]/55 to-transparent`}
          />
        </MobileParallax>

        <div className={`relative lg:col-span-6 ${reversed ? "lg:order-1" : ""}`}>
          {/* Desktop-only title — aligned with image top */}
          <div className="hidden lg:block mb-8 relative z-10">
            {service.key === "ceramic" && (
              <div
                data-testid="hot-topic-badge-desktop"
                className="inline-flex items-center gap-3.5 mb-6"
              >
                <span className="relative flex h-5 w-5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-80 animate-ping-wide" />
                  <span className="relative inline-flex h-5 w-5 rounded-full bg-red-500" />
                </span>
                <span className="text-lg uppercase tracking-[0.3em] text-red-500 font-bold">
                  Hot Topic
                </span>
              </div>
            )}
            <div className="flex items-center gap-3 mb-5">
              <span className="font-mono text-xs text-[#d4af37]">{num}</span>
              <span className="block w-8 h-px bg-[#d4af37]/60" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-white/50">
                {service.eyebrow}
              </span>
            </div>
            <h3 className="font-display font-bold text-6xl xl:text-[72px] tracking-tighter leading-[0.92] text-white">
              {service.title}
            </h3>
          </div>

          <p className="relative z-10 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl">
            <span className="block text-white/95 font-medium mb-1">
              Ideal for:
            </span>
            …{service.idealFor}
          </p>

          <ul className="relative z-10 mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 max-w-2xl">
            {service.bullets.map((b) => (
              <li
                key={b}
                className="flex items-start gap-2.5 text-sm sm:text-[15px] text-white/85"
              >
                <Check
                  size={16}
                  strokeWidth={2.25}
                  className="text-[#d4af37] mt-[3px] shrink-0"
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="relative z-10 mt-9 flex flex-col sm:flex-row sm:items-end gap-6 sm:gap-10">
            <a
              data-testid={`service-cta-${service.key}`}
              href={waLink(service.quoteMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#d4af37] hover:bg-[#f2c94c] text-black font-semibold px-7 py-3.5 rounded-full transition-colors w-fit"
            >
              {service.ctaLabel || "Request a quote"}
              <ArrowUpRight size={16} />
            </a>

            {service.key === "ceramic" && (
              <button
                type="button"
                data-testid="ceramic-more-info"
                onClick={onMoreInfo}
                className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/20 hover:border-white/40 text-white font-semibold px-7 py-3.5 rounded-full transition-colors w-fit backdrop-blur-md"
              >
                More info
              </button>
            )}

            {service.price && typeof service.price === "object" && (
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">
                  Starting at
                </div>
                <div className="font-display font-extrabold text-5xl sm:text-6xl text-[#d4af37] tracking-tighter">
                  {service.price.from}
                </div>
                <div className="text-sm text-white/55 mt-1">
                  up to{" "}
                  <span className="text-white/80 font-semibold">
                    {service.price.to}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function AddonPricing({ addon }) {
  if (addon.quoteCta) {
    return (
      <div className="space-y-2.5">
        <div className="text-sm text-white/70">Request a quote</div>
        <a
          data-testid={`addon-cta-${addon.name.replace(/\s/g, "-").toLowerCase()}`}
          href={waLink(addon.quoteMessage)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center bg-[#d4af37] hover:bg-[#f2c94c] text-black text-xs font-semibold px-5 py-2 rounded-full transition-colors"
        >
          Get a Quote
        </a>
      </div>
    );
  }

  if (addon.priceFrom) {
    return (
      <div className="leading-tight">
        <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mb-1">
          Starting at
        </div>
        <div className="font-display font-bold text-2xl text-[#d4af37]">
          {addon.priceFrom}
        </div>
        <div className="text-[11px] text-white/55 mt-1">
          up to{" "}
          <span className="text-white/85 font-semibold">{addon.priceTo}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="font-display font-bold text-2xl text-[#d4af37]">
      {addon.price}
    </div>
  );
}

export default function Services() {
  const [openAddon, setOpenAddon] = useState(null);
  const [originRect, setOriginRect] = useState(null);
  const [feynlabOpen, setFeynlabOpen] = useState(false);
  const openIcon = openAddon
    ? ADDITIONAL_ICONS[openAddon.name] || Plus
    : null;

  // ── Demo "tap" animation when add-ons grid scrolls into view ──
  const addonsRef = useRef(null);
  const addonsInView = useInView(addonsRef, { once: true, margin: "-15%" });
  const [demoActive, setDemoActive] = useState(false);
  useEffect(() => {
    if (!addonsInView || demoActive) return;
    const start = setTimeout(() => setDemoActive(true), 400);
    const stop = setTimeout(() => setDemoActive(false), 400 + 2000);
    return () => {
      clearTimeout(start);
      clearTimeout(stop);
    };
  }, [addonsInView, demoActive]);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 lg:py-36 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-10 lg:mb-14">
          <span className="text-xs uppercase tracking-[0.28em] text-[#d4af37] font-medium">
            ONE YORK
          </span>
          <h2 className="mt-4 font-display font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-[96px] tracking-tighter leading-[0.92] text-white">
            <span className="sr-only">Mobile Detailing &amp; Ceramic Coating </span>
            Services.
          </h2>
        </div>

        <div>
          {SERVICES.map((s, i) => (
            <ServiceRow
              key={s.key}
              service={s}
              index={i}
              onMoreInfo={() => setFeynlabOpen(true)}
            />
          ))}
        </div>

        {/* Additional services */}
        <div id="addons" className="mt-16 lg:mt-24 border-t border-white/10 pt-14 lg:pt-20 scroll-mt-24 lg:scroll-mt-28">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 lg:mb-14">
            <div>
              <span className="text-xs uppercase tracking-[0.28em] text-[#d4af37] font-medium">
                Add-ons
              </span>
              <h3 className="mt-3 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[0.95]">
                Additional{" "}
                <span className="italic font-medium text-white/70">
                  Services.
                </span>
              </h3>
            </div>
            <p className="text-sm text-white/55 max-w-sm">
              Premium add-ons available with any detailing package — booked
              alongside your main service.
            </p>
          </div>

          <div
            ref={addonsRef}
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 relative"
          >
            {/* Animated "tap to reveal" hint */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="col-span-2 lg:col-span-4 -mt-1 mb-2 flex items-center gap-3 text-[11px] sm:text-xs uppercase tracking-[0.22em] text-white/55"
            >
              <span className="relative inline-flex items-center justify-center w-7 h-7">
                <motion.span
                  className="absolute inset-0 rounded-full border border-[#d4af37]/40"
                  animate={{ scale: [1, 1.45, 1], opacity: [0.6, 0, 0.6] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                />
                <motion.svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-[#d4af37]"
                  animate={{ scale: [1, 0.82, 1], y: [0, 1, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                >
                  {/* index-finger / tap cursor */}
                  <path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11" />
                  <path d="M12 11V8.5a1.5 1.5 0 0 1 3 0V12" />
                  <path d="M15 12v-1a1.5 1.5 0 0 1 3 0v5.5a4.5 4.5 0 0 1-4.5 4.5h-2a4.5 4.5 0 0 1-4.5-4.5V13a1.5 1.5 0 0 1 3 0v1" />
                </motion.svg>
              </span>
              <span>
                <span className="text-white/85 font-medium tracking-[0.18em]">
                  Click any card
                </span>{" "}
                — reveal value &amp; benefit
              </span>
            </motion.div>

            {ADDITIONAL_SERVICES.map((a, i) => {
              const Icon = ADDITIONAL_ICONS[a.name] || Plus;
              return (
                <motion.button
                  key={a.name}
                  type="button"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setOriginRect({
                      left: rect.left,
                      top: rect.top,
                      width: rect.width,
                      height: rect.height,
                    });
                    setOpenAddon(a);
                  }}
                  data-testid={`addon-card-${a.name
                    .replace(/\s/g, "-")
                    .toLowerCase()}`}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 4) * 0.04 }}
                  animate={
                    i === 0 && demoActive
                      ? {
                          scale: [1, 1, 0.97, 1, 1],
                        }
                      : {}
                  }
                  transition={
                    i === 0 && demoActive
                      ? {
                          duration: 2,
                          times: [0, 0.35, 0.5, 0.7, 1],
                          ease: "easeOut",
                        }
                      : { duration: 0.5, delay: (i % 4) * 0.04 }
                  }
                  className="group relative text-left border border-[#d4af37]/55 hover:border-[#d4af37] from-[#0a0a0a] to-[#050505] hover:from-[#111] hover:to-[#080808] bg-gradient-to-br px-5 py-6 lg:px-6 lg:py-7 rounded-2xl transition-all duration-300 overflow-hidden flex flex-col cursor-pointer"
                >
                  {a.popular && (
                    <span className="absolute top-3 right-3 bg-[#dc2626] text-white text-[9px] font-semibold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}

                  <div className="flex items-start justify-between gap-3">
                    <div className="w-10 h-10 lg:w-11 lg:h-11 rounded-full border border-[#d4af37]/30 group-hover:border-[#d4af37] flex items-center justify-center shrink-0 transition-colors text-[#d4af37]">
                      <Icon
                        width={18}
                        height={18}
                        size={18}
                        strokeWidth={1.5}
                      />
                    </div>
                    {!a.popular && (
                      <span className="font-mono text-[10px] text-white/25 group-hover:text-[#d4af37]/60 transition-colors">
                        0{i + 1}
                      </span>
                    )}
                  </div>

                  <div className="mt-5 lg:mt-6 font-display font-semibold text-base sm:text-lg leading-tight text-white group-hover:text-[#d4af37] transition-colors">
                    {a.name}
                  </div>

                  {/* Pricing / CTA block */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <AddonPricing addon={a} />
                  </div>

                  <div className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full bg-[#d4af37]/60 transition-all duration-500" />

                  {/* Tapping hand — only over the FIRST card, 2s total */}
                  {i === 0 && demoActive && (
                    <motion.div
                      aria-hidden
                      initial={{ opacity: 0, y: -22, scale: 1 }}
                      animate={{
                        opacity: [0, 1, 1, 1, 0],
                        y: [-22, -4, 8, -4, -10],
                        scale: [1, 1, 0.85, 1, 1],
                      }}
                      transition={{
                        duration: 2,
                        times: [0, 0.2, 0.5, 0.75, 1],
                        ease: "easeOut",
                      }}
                      className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 flex items-center justify-center"
                    >
                      {/* Ripple ring on press */}
                      <motion.span
                        className="absolute w-14 h-14 rounded-full border-2 border-[#d4af37]"
                        initial={{ scale: 0.4, opacity: 0 }}
                        animate={{
                          scale: [0.4, 0.4, 2.2, 0.4],
                          opacity: [0, 0, 0.9, 0],
                        }}
                        transition={{
                          duration: 2,
                          times: [0, 0.45, 0.65, 0.75],
                          ease: "easeOut",
                        }}
                      />
                      {/* Hand — pointing finger */}
                      <svg
                        width="46"
                        height="46"
                        viewBox="0 0 24 24"
                        fill="#f4d774"
                        stroke="#1a1a1a"
                        strokeWidth="0.9"
                        strokeLinejoin="round"
                        style={{
                          filter:
                            "drop-shadow(0 6px 18px rgba(0,0,0,0.6)) drop-shadow(0 0 2px rgba(244,215,116,0.45))",
                        }}
                      >
                        {/* index finger + folded knuckles + palm */}
                        <path d="M11 3.5a1.5 1.5 0 0 1 3 0v7.2h.6a1.5 1.5 0 0 1 1.5 1.5v.3a1.5 1.5 0 0 1 1.5 1.5v.4a1.5 1.5 0 0 1 1.5 1.5v2.6a4 4 0 0 1-4 4h-2.4a4.6 4.6 0 0 1-3.6-1.8L6.1 16a1.2 1.2 0 0 1 1.9-1.45l1.5 1.55a.35.35 0 0 0 .6-.25V3.5z" />
                      </svg>
                    </motion.div>
                  )}
                </motion.button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Liquid-glass detail modal */}
      <AddonModal
        service={openAddon}
        originRect={originRect}
        onClose={() => setOpenAddon(null)}
        IconComp={openIcon}
      />

      {/* FEYNLAB info modal */}
      <FeynlabModal
        open={feynlabOpen}
        onClose={() => setFeynlabOpen(false)}
      />
    </section>
  );
}
